import React, { useEffect, useState, useRef, Fragment } from "react";
import { Box, Typography, Stack, TextField, Button, Card, CircularProgress, Grid2 as Grid, Popover } from "@mui/material";
import { sendMessage, getMessages } from "../../../config/apisauce";
import { format, isSameDay, isToday } from "date-fns"; // Import date-fns for formatting dates
import { ArrowLeft, CheckCircle, PaperPlaneTilt } from "@phosphor-icons/react";
import { GenericAvatar } from "../../../components/generic/GenericAvatar";
import { GenericDivider } from "../../../components/generic/GenericDivider";
import { pushToArray } from "../../../helpers/arrayHelper";
import useStore from "../../../states/store";
import { PAGE_PERMISSIONS } from "../../../enums/permissions/permission";
import { SHADOWS } from "../../../enums/themeEnum";
import { Link } from "@inertiajs/react";
import { toSlug } from "../../../helpers/stringHelper";

const { CAN_VIEW_ALL_CHATS } = PAGE_PERMISSIONS;

const InitialCollabMessageCard = ({ senderDetails, userId }) => {
  const requestor = senderDetails?.requested_by;
  const receiver = senderDetails?.requested_to;
  const isRequestSentByAuthUser = requestor?.id === userId;

  const message = isRequestSentByAuthUser
    ? `You have sent a collaboration request to ${receiver?.name}.`
    : `${requestor?.name} has sent you a collaboration request.`;

  return (
    <Box sx={{ backgroundColor: 'background.lightBlue', padding: 1.5, my: 2.5, borderRadius: '10px', }}>
      <Stack direction={'row'} gap={1} color={'primary.main'} alignItems={'center'} justifyContent={'center'}>
        <PaperPlaneTilt size={24} />
        <Typography color="grey.dark">{message}</Typography>
      </Stack>
    </Box>
  );
};

const ResultCollabMessageCard = ({ senderDetails, userId }) => {
  const requestor = senderDetails?.requested_by;
  const receiver = senderDetails?.requested_to;
  const isRequestSentByAuthUser = requestor?.id === userId;

  const message = isRequestSentByAuthUser
    ? `${receiver?.name} has accepted your collaboration request.`
    : `You have accepted the collaboration request from ${requestor?.name}`;

  return (
    <Box sx={{ backgroundColor: 'background.lightBlue', padding: 1.5, my: 2.5, borderRadius: '10px', }}>
      <Stack direction={'row'} gap={1} color={'primary.main'} alignItems={'center'} justifyContent={'center'}>
        <CheckCircle size={24} />
        <Typography color="grey.dark">{message}</Typography>
      </Stack>
    </Box>
  );
};

const CollabMessageCard = ({ senderDetails, userId }) => {
  const isAdmin = !(senderDetails?.requested_by?.id === userId || senderDetails?.requested_to?.id === userId);

  if (isAdmin) {
    return (
      <Box sx={{ backgroundColor: 'background.lightBlue', padding: 1.5, my: 2.5, borderRadius: '10px', }}>
        <Stack direction={'row'} gap={1} color={'primary.main'} alignItems={'center'} justifyContent={'center'}>
          <CheckCircle size={24} />
          <Typography color="grey.dark" sx={{ display: 'inline-flex', flexWrap: 'wrap' }}>
            <Box component="span" color={"darkslategray"} fontWeight={600}>
              {senderDetails?.requested_by?.name}
            </Box>
            &nbsp;has requested a collaboration with&nbsp;
            <Box component="span" color={"darkslategray"} fontWeight={600}>
              {senderDetails?.requested_to?.name}.
            </Box>
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box>
      <InitialCollabMessageCard senderDetails={senderDetails} userId={userId} />
      <ResultCollabMessageCard senderDetails={senderDetails} userId={userId} />
    </Box>
  );
};

const DateLabel = ({ date }) => {
  const formattedDate = isToday(new Date(date)) ? 'Today' : format(new Date(date), 'MMMM d, yyyy');
  return (
    <GenericDivider>
      <Typography variant='caption' color='grey.dark'>
        {formattedDate}
      </Typography>
    </GenericDivider>
  );
};

const ChatMessages = ({ creatorDetails, senderDetails, userId, setChatListModel, isSm, setSenderDetails }) => {
  const [, { hasPermission }] = useStore();
  const [messages, setMessages] = useState();
  const [newMessage, setNewMessage] = useState("");
  const messagesEndRef = useRef(null);
  const [pagination, setPagination] = useState({ pageSize: 10, page: 0 });
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const prevHeightRef = useRef(0);
  const requestDetails = (userId === senderDetails?.requested_by?.id) ? senderDetails?.requested_to : senderDetails?.requested_by;

  const scrollToBottom = () => {
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
  };

  const fetchMessages = async () => {
    setIsLoadingMessages(true);
    const { ok, data } = await getMessages({
      sender_id: senderDetails?.requested_by?.id,
      receiver_id: senderDetails?.requested_to?.id,
      page: pagination?.page,
      per_page: pagination?.pageSize
    });

    if (ok) {
      setMessages({ ...data, data: [...data.data].reverse() });
    }

    setIsLoadingMessages(false);
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    const params = {
      sender_id: userId,
      receiver_id: requestDetails?.id,
      message: newMessage,
      request_id: senderDetails?.id
    };

    const { ok, data } = await sendMessage(params);
    if (ok) {
      setNewMessage("");
      setMessages((prevMessages) => {
        const currentList = { ...prevMessages };
        currentList.data = pushToArray(currentList?.data, data);
        return currentList;
      });
      setChatListModel((prevModel) => {
        const updatedData = prevModel?.data?.map(chat => {
          if (chat?.id === data?.request_id) {
            return {
              ...chat,
              latest_message: data
            };
          }
          return chat;
        });

        return {
          ...prevModel,
          data: [
            updatedData.find(chat => chat?.id === data?.request_id),
            ...updatedData.filter(chat => chat?.id !== data?.request_id)
          ]
        };
      });

      scrollToBottom();
    }
  };

  const connectWebSocket = () => {
    const chatChannel = window.Echo.private(`chat.${userId}`);
    chatChannel.listen("MessageSent", (messageDetails) => {
      if (messageDetails?.message) {
        if (messageDetails?.request_id === senderDetails?.id) {
          setMessages((prevMessages) => {
            const currentList = { ...prevMessages };
            currentList.data = pushToArray(currentList?.data, messageDetails);
            return currentList;
          });
          scrollToBottom();
        }
      }
    });
    return () => {
      chatChannel.stopListening("MessageSent");
    };
  };

  const handleScroll = (event) => {
    const chatContainer = event.target;
    const isAtTop = chatContainer.scrollTop === 0;

    if (isAtTop && !isLoadingMessages) {
      prevHeightRef.current = chatContainer.scrollHeight;
      setPagination((prev) => ({
        ...prev,
        pageSize: prev.pageSize + 10,
      }));
    }
  };

  useEffect(() => {
    const chatContainer = messagesEndRef.current?.parentNode;
    if (!chatContainer) return;

    const newHeight = chatContainer.scrollHeight;

    if (pagination.pageSize <= 10 && !prevHeightRef.current) {
      scrollToBottom();
    } else if (prevHeightRef.current) {
      const heightDiff = newHeight - prevHeightRef.current;
      chatContainer.scrollTop += heightDiff;
    }
  }, [messages]);

  useEffect(() => {
    setPagination({ pageSize: 10, page: 0 });
    messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
  }, [senderDetails, creatorDetails]);

  useEffect(() => {
    fetchMessages();
  }, [pagination]);

  useEffect(() => {
    const cleanup = connectWebSocket();
    return () => {
      if (cleanup) cleanup();
    };
  }, [senderDetails?.id]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", padding: 0 }}>
      <Box sx={{ padding: 2, position: "sticky" }}>
        <Stack direction={"row"} alignItems={"center"} gap={2.5}>
          {isSm && <ArrowLeft size={20} weight="light" color="#637381" onClick={() => setSenderDetails(null)} />}
          {!hasPermission(CAN_VIEW_ALL_CHATS) ? (
            <GenericAvatar size={isSm ? "32px" : "48px"} location={requestDetails?.details?.profile_picture?.location} />
          ) : (
            <Box>
              <Box sx={{ position: "relative", width: "50px", height: "46px" }}>
                <Box sx={{ position: "absolute", left: 0, top: -5, zIndex: 2 }}>
                  <GenericAvatar size="32px" location={senderDetails?.requested_by?.details?.profile_picture?.location} />
                </Box>
                <Box sx={{ position: "absolute", left: 20, bottom: 5, zIndex: 1 }}>
                  <GenericAvatar size="32px" location={senderDetails?.requested_to?.details?.profile_picture?.location} />
                </Box>
              </Box>
            </Box>
          )}
          <Typography variant="h6" fontWeight={800}>
            {requestDetails?.name || "Unknown"}
          </Typography>
        </Stack>
      </Box>

      <GenericDivider margin={"0px"} />

      <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", overflowY: "auto" }}>
        {isLoadingMessages && (
          <Box sx={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Card sx={{ backgroundColor: "primary.main", padding: "6px", color: "#FFF", mt: 1 }}>
              <Stack alignItems={"center"} justifyContent={"center"} direction={"row"} gap={1}>
                <CircularProgress size={16} color="#FFF" thickness={5} />
                <Typography variant="caption">Loading earlier messages...</Typography>
              </Stack>
            </Card>
          </Box>
        )}
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column", overflowY: "hidden" }}>
          <Box
            onScroll={messages?.per_page < messages?.total ? handleScroll : undefined}
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              padding: 2,
            }}
          >
            {messages?.data?.length > 0 ? (
              messages?.data?.map((message, index) => {
                const currentDate = format(new Date(message?.created_at), "yyyy-MM-dd");
                const previousDate = index > 0 ? format(new Date(messages?.data[index - 1].created_at), "yyyy-MM-dd") : null;
                const isSameDate = !isSameDay(currentDate, previousDate);
                const isSender = message?.sender_id === userId;
                return (
                  <Fragment key={`message-key-${index}`}>
                    {isSameDate && <DateLabel date={message?.created_at} />}
                    {pagination.pageSize >= messages?.total && index === 0 && <CollabMessageCard senderDetails={senderDetails} userId={userId} />}
                    <Stack direction={isSender ? "row-reverse" : "row"} gap={1.5} alignItems="flex-end" mb={1.5}>
                      <ChatProfilePopover message={message} isSender={isSender} />
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: `15px 15px ${isSender ? '3px' : '15px'} ${isSender ? '15px' : '3px'}`,
                          maxWidth: isSm ? "100%" : "50%",
                          backgroundColor: isSender ? "primary.main" : "grey.main",
                          color: isSender ? "#FFF" : "#637381",
                        }}
                      >
                        <Box sx={{ display: "flex", flexDirection: !isSender ? "row" : "row-reverse", gap: 1.5 }}>
                          <Typography variant="caption" sx={{ fontWeight: 800 }}>
                            {isSender ? "Me" : message?.sender?.name}
                          </Typography>
                          <Typography variant="caption" color={isSender ? '#FFF' : "grey.dark"} fontWeight={400}>
                            {new Date(message?.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          </Typography>
                        </Box>
                        <Typography>{message?.message}</Typography>
                      </Box>
                    </Stack>
                  </Fragment>
                );
              })
            ) : (
              <Fragment>
                <DateLabel date={senderDetails?.created_at} />
                <CollabMessageCard  senderDetails={senderDetails} userId={userId}/>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "50vh", padding: 2.5 }}>
                  <Typography textAlign={"center"} color="gray">
                    Send a message to start a conversation!
                  </Typography>
                </Box>
              </Fragment>
            )}
            <div ref={messagesEndRef} />
          </Box>
          {!hasPermission(CAN_VIEW_ALL_CHATS) && (
            <Fragment>
              <GenericDivider margin={"0px"} />
              <Box sx={{ padding: 2 }} component="label" htmlFor="chat-input">
                <Grid container spacing={1} direction={"column"}>
                  <Grid item size={12}>
                    <TextField
                      id="chat-input"
                      fullWidth
                      multiline
                      minRows={1}
                      maxRows={4}
                      placeholder="Type a message..."
                      variant="standard"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                      slotProps={{
                        input: {
                          disableUnderline: true,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item size={12}>
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <Button variant="contained" onClick={() => handleSendMessage()} disabled={!newMessage}>
                        Send
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Fragment>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ChatMessages;

const ChatProfilePopover = ({ message, isSender }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? `profile-popover-${message.id}` : undefined;

  return (
    <Box>
      <Button
        sx={{ display: "inline-flex", minWidth: "auto", padding: 0 }}
        aria-describedby={id}
        variant="contained"
        onClick={handleClick}
      >
        <GenericAvatar location={message?.sender?.details?.profile_picture?.location} />
      </Button>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: isSender ? 'right' : 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: isSender ? 'right' : 'left',
        }}
        sx={{
          ".MuiPaper-root": {
            boxShadow: SHADOWS.light,
            px: 1.5,
            py: 1,
            marginTop: 1,
          },
        }}
      >
        <Link
          href={`/creator?user=${toSlug(message?.sender?.name)}-${message?.sender?.id}`}
          style={{ textDecoration: "none", color: "inherit" }}
        >
          <Typography>View Profile</Typography>
        </Link>
      </Popover>
    </Box>
  );
};
